import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';
import ConfirmDialog from '../components/ConfirmDialog';

interface Partner {
  id: string;
  name: string;
  logoPath: string;
  person?: string;
  email?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

type SortField = 'name' | 'person' | 'email';
type SortDirection = 'asc' | 'desc';

const client = generateClient<Schema>({ authMode: 'userPool' });

interface AdminPartnersProps {
  onEditPartner?: (partnerId: string) => void;
  refreshTrigger?: number; // When this changes, refresh the list
}

const AdminPartners: React.FC<AdminPartnersProps> = ({ onEditPartner, refreshTrigger }) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; partnerId: string; partnerName: string }>({ isOpen: false, partnerId: '', partnerName: '' });
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchPartners();
    }
  }, [refreshTrigger]);

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Inject search into header (fixed to prevent recreation on every keystroke)
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      // Check if search field already exists
      let searchInput = container.querySelector('#partnersSearch') as HTMLInputElement;
      let clearButton = container.querySelector('#partnersClear') as HTMLButtonElement;
      
      if (!searchInput) {
        // Create the search field only if it doesn't exist
        container.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <input
              id="partnersSearch"
              type="text"
              placeholder="Search partners..."
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 250px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            />
            <button
              id="partnersClear"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; background: rgba(255, 255, 255, 0.95); color: #374151; cursor: pointer; font-weight: 500; display: none;"
            >
              Clear
            </button>
          </div>
        `;

        // Get the newly created elements
        searchInput = container.querySelector('#partnersSearch') as HTMLInputElement;
        clearButton = container.querySelector('#partnersClear') as HTMLButtonElement;

        // Add event listeners only once
        if (searchInput) {
          searchInput.addEventListener('input', (e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchTerm(value);
            setCurrentPage(1);
            
            // Show/hide clear button
            if (clearButton) {
              clearButton.style.display = value ? 'block' : 'none';
            }
          });
          
          searchInput.addEventListener('focus', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.6)';
            (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
          });
          
          searchInput.addEventListener('blur', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.3)';
            (e.target as HTMLInputElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          });
        }

        if (clearButton) {
          clearButton.addEventListener('click', () => {
            setSearchTerm('');
            setCurrentPage(1);
            if (searchInput) {
              searchInput.value = '';
              searchInput.focus();
            }
            clearButton.style.display = 'none';
          });
        }
      }
      
      // Update the input value and clear button visibility without recreating
      if (searchInput) {
        searchInput.value = searchTerm;
      }
      if (clearButton) {
        clearButton.style.display = searchTerm ? 'block' : 'none';
      }
    }

    return () => {
      // Cleanup only when component unmounts
      const container = document.getElementById('header-selector-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []); // Empty dependency array - only run once

  // Separate effect to update the search input value without recreating the DOM
  useEffect(() => {
    const searchInput = document.getElementById('partnersSearch') as HTMLInputElement;
    const clearButton = document.getElementById('partnersClear') as HTMLButtonElement;
    
    if (searchInput && searchInput.value !== searchTerm) {
      searchInput.value = searchTerm;
    }
    if (clearButton) {
      clearButton.style.display = searchTerm ? 'block' : 'none';
    }
  }, [searchTerm]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      console.log('Fetching partners from database...');
      
      // Fetch ALL partners with pagination
      let allPartners: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.Partner.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allPartners = [...allPartners, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminPartners: Fetched ${response.data?.length || 0} partners (total: ${allPartners.length})`);
      } while (nextToken);
      
      console.log(`✅ AdminPartners: Total partners fetched: ${allPartners.length}`);
      
      // Convert to our interface format
      const partnersData = allPartners.map((partner: any) => ({
        id: partner.id,
        name: partner.name || '',
        logoPath: partner.logoPath || '',
        person: partner.person || '',
        email: partner.email || '',
        description: partner.description || '',
        createdAt: partner.createdAt || new Date().toISOString(),
        updatedAt: partner.updatedAt || new Date().toISOString()
      }));
      
      setPartners(partnersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError(`Failed to fetch partners: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Fallback to empty array instead of sample data
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };



  const handleDeletePartner = (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    if (partner) {
      setConfirmDialog({
        isOpen: true,
        partnerId,
        partnerName: partner.name
      });
    }
  };

  const confirmDeletePartner = async () => {
    const { partnerId } = confirmDialog;
    setConfirmDialog({ isOpen: false, partnerId: '', partnerName: '' });

    try {
      console.log('🗑️ Soft deleting partner with ID:', partnerId);
      
      // SOFT DELETE: Set status to 'deleted' and add deletedAt timestamp
      // This preserves data immutability - never hard delete user data
      await client.models.Partner.update({ 
        id: partnerId,
        status: 'deleted',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin' // TODO: Get actual admin user email
      });
      console.log('✅ Partner soft deleted successfully');
      
      // Remove from local state
      const updatedPartners = partners.filter(p => p.id !== partnerId);
      setPartners(updatedPartners);
      
      setMessage({ type: 'success', text: 'Partner deleted successfully!' });
    } catch (err) {
      console.error('❌ Error deleting partner:', err);
      setMessage({ type: 'error', text: 'Failed to delete partner. Please try again.' });
    }
  };

  const sortedPartners = [...partners].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle undefined/null values
    if (!aValue) aValue = '';
    if (!bValue) bValue = '';

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Filter partners based on search term
  const filteredPartners = sortedPartners.filter(partner => {
    const searchLower = searchTerm.toLowerCase();
    return (
      partner.name.toLowerCase().includes(searchLower) ||
      (partner.person && partner.person.toLowerCase().includes(searchLower)) ||
      (partner.email && partner.email.toLowerCase().includes(searchLower)) ||
      (partner.description && partner.description.toLowerCase().includes(searchLower))
    );
  });

  // Pagination logic
  const totalItems = filteredPartners.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPartners = filteredPartners.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading partners...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchPartners}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-card" style={{ padding: 0, paddingBottom: '60px' }}>
      {message && (
        <div style={{ padding: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}

      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Partner"
        message={`Are you sure you want to delete the partner "${confirmDialog.partnerName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDeletePartner}
        onCancel={() => setConfirmDialog({ isOpen: false, partnerId: '', partnerName: '' })}
      />
      {filteredPartners.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <p>
            {searchTerm 
              ? `No partners found matching "${searchTerm}". Try a different search term.`
              : 'No partners found. Create your first partner.'
            }
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', margin: 0, padding: 0 }}>
            <table className="admin-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Partner Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('person')} style={{ cursor: 'pointer' }}>
                  Contact Person {sortField === 'person' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                  Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Logo</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPartners.map((partner) => (
                <tr key={partner.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                      {partner.name}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                      {partner.person || '-'}
                    </div>
                  </td>
                  <td>
                    {partner.email ? (
                      <a 
                        href={`mailto:${partner.email}`}
                        style={{ color: '#008F99', textDecoration: 'none' }}
                      >
                        {partner.email}
                      </a>
                    ) : '-'}
                  </td>
                  <td>
                    <div
                      title={partner.logoPath ? 'Logo selected' : 'No logo'}
                      aria-label={partner.logoPath ? 'Logo selected' : 'No logo'}
                      style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '50%',
                        background: partner.logoPath ? '#008F99' : '#d1d5db',
                        boxShadow: partner.logoPath ? '0 0 0 2px rgba(0,143,153,0.15)' : 'none',
                        margin: '0 auto'
                      }}
                    />
                  </td>
                  <td>
                    <div className="actions">
                      {onEditPartner && (
                        <button
                          onClick={() => onEditPartner(partner.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #8B5A2B',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#8B5A2B',
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePartner(partner.id)}
                        className="danger"
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #dc2626',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#dc2626',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: currentPage === 1 ? '#f9fafb' : 'white',
              color: currentPage === 1 ? '#9ca3af' : '#374151',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: page === currentPage ? '#008F99' : 'white',
                color: page === currentPage ? 'white' : '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: page === currentPage ? '600' : '400'
              }}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: currentPage === totalPages ? '#f9fafb' : 'white',
              color: currentPage === totalPages ? '#9ca3af' : '#374151',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      )}
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );


};

export default AdminPartners;
